import { useCallback, useEffect, useState } from "react";
import {
	dismissPwaInstallPrompt,
	isAndroidDevice,
	isIosDevice,
	isMobileDevice,
	isPwaStandalone,
	wasPwaInstallDismissedRecently,
} from "./pwa-install";

const DEFAULT_DELAY_MS = 2500;

/**
 * Detects home-screen / standalone launch and captures Chromium's
 * beforeinstallprompt so we can show a branded install UI.
 */
export const usePwaInstall = ({ delayMs = DEFAULT_DELAY_MS } = {}) => {
	const [isStandalone, setIsStandalone] = useState(isPwaStandalone);
	const [deferredPrompt, setDeferredPrompt] = useState(null);
	const [isIos] = useState(isIosDevice);
	const [isAndroid] = useState(isAndroidDevice);
	const [isMobile] = useState(isMobileDevice);
	const [dismissed, setDismissed] = useState(wasPwaInstallDismissedRecently);
	const [delayElapsed, setDelayElapsed] = useState(false);
	const [showIosHelp, setShowIosHelp] = useState(false);

	useEffect(() => {
		const media =
			typeof window !== "undefined" && window.matchMedia
				? window.matchMedia("(display-mode: standalone)")
				: null;

		const syncStandalone = () => setIsStandalone(isPwaStandalone());

		syncStandalone();

		if (media) {
			if (media.addEventListener) {
				media.addEventListener("change", syncStandalone);
			} else if (media.addListener) {
				media.addListener(syncStandalone);
			}
		}

		return () => {
			if (!media) {
				return;
			}
			if (media.removeEventListener) {
				media.removeEventListener("change", syncStandalone);
			} else if (media.removeListener) {
				media.removeListener(syncStandalone);
			}
		};
	}, []);

	useEffect(() => {
		const onBeforeInstallPrompt = (event) => {
			event.preventDefault();
			setDeferredPrompt(event);
		};

		const onAppInstalled = () => {
			setDeferredPrompt(null);
			setIsStandalone(true);
			dismissPwaInstallPrompt();
			setDismissed(true);
			setShowIosHelp(false);
		};

		window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
		window.addEventListener("appinstalled", onAppInstalled);

		return () => {
			window.removeEventListener(
				"beforeinstallprompt",
				onBeforeInstallPrompt
			);
			window.removeEventListener("appinstalled", onAppInstalled);
		};
	}, []);

	useEffect(() => {
		const timer = window.setTimeout(() => setDelayElapsed(true), delayMs);
		return () => window.clearTimeout(timer);
	}, [delayMs]);

	const canPromptChromium = Boolean(deferredPrompt);
	const canPromptIos = isIos && !isStandalone;
	const shouldOfferInstall =
		isMobile &&
		!isStandalone &&
		!dismissed &&
		(canPromptChromium || canPromptIos);

	const open = delayElapsed && shouldOfferInstall;

	const dismiss = useCallback(() => {
		dismissPwaInstallPrompt();
		setDismissed(true);
		setShowIosHelp(false);
	}, []);

	const install = useCallback(async () => {
		if (deferredPrompt) {
			deferredPrompt.prompt();
			try {
				const choice = await deferredPrompt.userChoice;
				setDeferredPrompt(null);
				if (choice && choice.outcome === "accepted") {
					dismissPwaInstallPrompt();
					setDismissed(true);
				} else {
					dismiss();
				}
			} catch (error) {
				dismiss();
			}
			return;
		}

		if (isIos) {
			setShowIosHelp(true);
		}
	}, [deferredPrompt, dismiss, isIos]);

	return {
		open,
		isStandalone,
		isIos,
		isAndroid,
		isMobile,
		canInstall: canPromptChromium,
		showIosHelp,
		install,
		dismiss,
	};
};
